import { Box, Button, Card, Chip, Grid, Typography, Tooltip } from "@mui/material";
import { FieldArray, useFormikContext } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { McpServerDto } from "src/types/mcpServer";
import { McpServerSyncStatus } from "src/types/aiAgent";
import McpServerRow from "./McpServerRow";
import { addMcpServer } from "src/utils/mcpServerHelper";

interface McpServersSectionProps {
	allServers: McpServerDto[];
	syncStatus?: McpServerSyncStatus;
}

const McpServersSection: FC<McpServersSectionProps> = ({ allServers, syncStatus }) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<any>();

	const mcpServers: any[] = values.mcpServers || [];
	const assignedServerIds = mcpServers
		.filter((s: any) => Boolean(s.mcpServerId))
		.map((s: any) => s.mcpServerId);

	return (
		<Grid item xs={12}>
			<Card
				sx={{
					p: 3,
					mt: 2,
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
				}}
			>
				{/* Header */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						mb: 2,
					}}
				>
					<Typography variant="h6" fontWeight="bold">
						{t("McpServer.SectionTitle")}
					</Typography>
					{syncStatus === McpServerSyncStatus.Synced && (
						<Tooltip title={t("McpServer.SyncStatus.SyncedTooltip")} arrow>
							<Chip
								icon={<CheckCircleIcon />}
								label={t("McpServer.SyncStatus.Synced")}
								size="small"
								color="success"
								variant="outlined"
								sx={{ "& .MuiChip-label": { overflow: "visible" } }}
							/>
						</Tooltip>
					)}
					{syncStatus === McpServerSyncStatus.InProgress && (
						<Tooltip title={t("McpServer.SyncStatus.InProgressTooltip")} arrow>
							<Chip
								icon={<SyncIcon />}
								label={t("McpServer.SyncStatus.InProgress")}
								size="small"
								color="info"
								variant="outlined"
								sx={{ "& .MuiChip-label": { overflow: "visible" } }}
							/>
						</Tooltip>
					)}
					{syncStatus === McpServerSyncStatus.SyncFailed && (
						<Tooltip title={t("McpServer.SyncStatus.SyncFailedTooltip")} arrow>
							<Chip
								icon={<CancelIcon />}
								label={t("McpServer.SyncStatus.SyncFailed")}
								size="small"
								color="error"
								variant="outlined"
								sx={{ "& .MuiChip-label": { overflow: "visible" } }}
							/>
						</Tooltip>
					)}
					{syncStatus === McpServerSyncStatus.NotSynced && (
						<Tooltip title={t("McpServer.SyncStatus.NotSyncedTooltip")} arrow>
							<Chip
								icon={<ErrorIcon />}
								label={t("McpServer.SyncStatus.NotSynced")}
								size="small"
								color="warning"
								variant="outlined"
							/>
						</Tooltip>
					)}
					<Tooltip title={t("McpServer.TextChannelsOnly")} arrow>
						<InfoOutlinedIcon
							fontSize="small"
							color="action"
							sx={{ cursor: "pointer" }}
						/>
					</Tooltip>
				</Box>

				{/* MCP Server rows via FieldArray */}
				<FieldArray
					name="mcpServers"
					render={(arrayHelpers) => (
						<>
							{mcpServers.map((server: any, index: number) => (
								<McpServerRow
									key={server.id || index}
									server={server}
									index={index}
									availableServers={allServers}
									onDelete={() => arrayHelpers.remove(index)}
									assignedServerIds={assignedServerIds}
								/>
							))}
							<Button
								color="primary"
								startIcon={<AddCircleIcon />}
								variant="contained"
								onClick={() => addMcpServer(arrayHelpers)}
								sx={{ mt: 1 }}
							>
								{t("Shared.Add")}
							</Button>
						</>
					)}
				/>
			</Card>
		</Grid>
	);
};

export default McpServersSection;
